import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { LuneTheme } from '@lune-fe/lune-ui-lib'
import type { SxProps } from '@mui/material'
import { Box } from '@mui/material'
import HorizontalSegmentedBar from 'lune-ui-lib/src/components/HorizontalSegmentedBar'

import {
    convertToGram,
    convertToKg,
    convertToTonne,
    type DisplayMassUnit,
    formatAdaptiveNumericValue,
} from '../../utils/utils'

import { EmissionsExplanationSectionProps } from './useExplanationSteps'

const { palette } = LuneTheme

export const WtwBreakdownExplanation = ({
    wtwDetails,
    emissionsUnit,
    sx,
    legendOptions = { ontop: false, vertical: true },
}: {
    wtwDetails: EmissionsExplanationSectionProps['emissions']['wtwDetails']
    emissionsUnit: DisplayMassUnit
    legendOptions?: {
        ontop: boolean
        vertical: boolean
    }
    sx?: SxProps
}) => {
    useLingui()

    const unitConversionMap = {
        g: convertToGram,
        kg: convertToKg,
        t: convertToTonne,
    }
    const unitConversion = unitConversionMap[emissionsUnit]
    const breakdownConvertedUnit = wtwDetails
        ? {
              wtt: unitConversion(wtwDetails.wtt),
              ttw: unitConversion(wtwDetails.ttw),
              unknown: unitConversion(wtwDetails.unknown),
          }
        : undefined
    const formatSegmentValue = (
        value: NonNullable<Parameters<typeof formatAdaptiveNumericValue>[0]>,
    ): string => formatAdaptiveNumericValue(value, '0')
    return (
        <Box sx={sx}>
            {breakdownConvertedUnit && (
                <HorizontalSegmentedBar
                    segments={[
                        {
                            value: breakdownConvertedUnit.wtt.toNumber(),
                            formattedValue: formatSegmentValue(breakdownConvertedUnit.wtt),
                            unit: `${emissionsUnit}CO₂e`,
                            colour: palette.Data100,
                            order: 0,
                            label: t`Well-To-Tank`,
                        },
                        {
                            value: breakdownConvertedUnit.ttw.toNumber(),
                            formattedValue: formatSegmentValue(breakdownConvertedUnit.ttw),
                            unit: `${emissionsUnit}CO₂e`,
                            colour: palette.Data300,
                            order: 1,
                            label: t`Tank-To-Wheel`,
                        },
                        ...(breakdownConvertedUnit.unknown.gt(0)
                            ? [
                                  {
                                      value: breakdownConvertedUnit.unknown.toNumber(),
                                      formattedValue: formatSegmentValue(
                                          breakdownConvertedUnit.unknown,
                                      ),
                                      unit: `${emissionsUnit}CO₂e`,
                                      colour: '#BDBDBD',
                                      order: 2,
                                      label: t`Unknown stage`,
                                  },
                              ]
                            : []),
                    ]}
                    legendOptions={legendOptions}
                />
            )}
        </Box>
    )
}

export default WtwBreakdownExplanation
